import React, { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../contexts/AuthContext';
import api, { AccessPass, PassId, Pricing } from '../services/api';
import { formatBillingDate } from '../utils/format';

const FEATURES = [
  'Unlimited VCE Methods and Specialist practice questions',
  'Personalized difficulty matching',
  'Detailed performance analytics',
  'Question library with year filtering',
  'Survival and Rush game modes',
  'Progress tracking and streaks',
];

const accessLabel = (pass: AccessPass) =>
  pass.expires_at ? `Access until ${formatBillingDate(pass.expires_at)}` : 'Access never expires';

const Subscription: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState<PassId | null>(null);
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await api.get<Pricing>('/subscriptions/pricing');
        setPricing(response.data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        setError('Could not load pricing. Please refresh the page.');
      }
    };

    fetchPricing();
  }, []);

  // Resume the purchase a signed-out visitor started before logging in.
  useEffect(() => {
    const intendedPass = localStorage.getItem('intended_plan');
    if (intendedPass && user) {
      localStorage.removeItem('intended_plan');
      handleBuy(intendedPass as PassId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleBuy = async (passId: PassId) => {
    setIsLoading(passId);
    setError(null);

    if (!user) {
      localStorage.setItem('intended_plan', passId);
      window.location.href = '/login';
      return;
    }

    try {
      const response = await api.post('/subscriptions/create-checkout-session', {
        pass_id: passId
      });
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error('Checkout error:', error);
      setIsLoading(null);

      if (error.response?.status === 403 || error.response?.status === 401) {
        localStorage.setItem('intended_plan', passId);
        window.location.href = '/login';
        return;
      }
      setError(error.response?.data?.detail || 'Could not start checkout. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            Choose Your Pass
          </h1>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
            One payment. No subscription. Prices in AUD.
          </p>
        </div>

        {error && (
          <p role="alert" className="mt-8 text-center text-red-600 dark:text-red-400">{error}</p>
        )}

        {/* Pricing Cards */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3 max-w-6xl mx-auto">
          {pricing?.passes.map((pass) => (
            <div
              key={pass.id}
              className="relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 shadow-sm"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {pass.name}
                </h3>

                <div className="mt-4">
                  <span className="text-5xl font-bold text-gray-900 dark:text-white">
                    {pass.price_display}
                  </span>
                  <span className="text-base font-medium text-gray-500 dark:text-gray-400">
                    {' '}{pricing.currency} once
                  </span>
                </div>

                <p className="mt-2 text-sm font-medium text-mango-600 dark:text-mango-400">
                  {accessLabel(pass)}
                </p>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {pass.description}
                </p>
              </div>

              <ul className="mt-8 space-y-3">
                {FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-mango-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleBuy(pass.id)}
                disabled={isLoading !== null}
                className="mt-8 w-full rounded-lg py-3 px-6 text-center text-sm font-medium transition-colors bg-mango-500 text-white hover:bg-mango-600 focus:ring-2 focus:ring-mango-500 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-offset-2"
              >
                {isLoading === pass.id ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </div>
                ) : (
                  `Buy ${pass.name}`
                )}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Is this a subscription?
              </h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                No. Each pass is a single payment that never renews. Year passes end at midnight on 1 December (Melbourne time). Buying a longer pass later extends your access.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                What payment methods do you accept?
              </h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                We accept all major credit cards (Visa, MasterCard, American Express) through our secure payment processor.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                How many practice questions are available?
              </h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Our platform includes thousands of VCE Methods and Specialist questions from past exams, categorized by topic and difficulty level.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
